import {
  afterNextRender,
  AfterViewInit,
  Component,
  computed,
  contentChild,
  DestroyRef,
  effect,
  inject,
  Injector,
  input,
  output,
  Signal,
  signal,
  TemplateRef,
  untracked,
  WritableSignal,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormsModule,
  NG_VALUE_ACCESSOR,
  NgControl,
  ReactiveFormsModule,
  ValidationErrors,
} from '@angular/forms';
import {
  MultiSelectChangeEvent,
  MultiSelectFilterEvent,
  MultiSelectLazyLoadEvent,
  MultiSelectModule,
} from 'primeng/multiselect';
import {Selectable} from '../../../../core/interfaces/selectable.interface';
import {AvailablePaginationSize} from '../../../../core/types/available-pagination-size.type';
import {Page} from '../../../../core/models/page-response.model';
import {NgTemplateOutlet} from '@angular/common';
import {Subject} from 'rxjs';
import {debounceTime, takeUntil} from 'rxjs/operators';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {ValidationErrorsDictionary} from '../../../../core/dictionary/validation-errors.dictionary';
import {FloatLabel} from 'primeng/floatlabel';
import {IftaLabel} from 'primeng/iftalabel';

export type MultiSelectVariant = 'outlined' | 'filled';
export type MultiSelectLabelMode = 'fixed' | 'float' | 'ifta';
export type MultiSelectSize = 'small' | 'large' | undefined;
export type MultiSelectDisplay = 'comma' | 'chip';
export type MultiSelectSelectionLimitDisplay = 'full' | 'truncated';

export interface MultiSelectSelectionChangeEvent<T extends Selectable<any>> {
  originalEvent: Event;
  value: T['value'][];
}

export interface MultiSelectRawSelectionChangeEvent<T> {
  originalEvent: Event;
  value: T[];
}

export interface MultiSelectLoadMoreEvent {
  page: number;
  size: AvailablePaginationSize;
  query: string | null;
}

@Component({
  selector: 'app-multiselect-input',
  imports: [ReactiveFormsModule, MultiSelectModule, NgTemplateOutlet, FormsModule, FloatLabel, IftaLabel],
  templateUrl: './multiselect-input.component.html',
  styleUrl: './multiselect-input.component.css',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: MultiSelectInputComponent,
      multi: true,
    },
  ],
})
export class MultiSelectInputComponent<T extends Selectable<TValue>, TValue = T['value']>
  implements ControlValueAccessor, AfterViewInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly injector = inject(Injector);

  private readonly destroy$ = new Subject<void>();
  private readonly filterSubject$ = new Subject<string | null>();

  readonly options = input.required<Page<T> | T[] | null>();
  readonly optionGroupLabel = input<string>('label');
  readonly optionGroupChildren = input<string>('items');
  readonly group = input<boolean>(false);

  readonly label = input<string>('');
  readonly placeholder = input<string>('');
  readonly variant = input<MultiSelectVariant>('outlined');
  readonly labelMode = input<MultiSelectLabelMode>('fixed');
  readonly size = input<MultiSelectSize>(undefined);
  readonly showClear = input<boolean>(false);

  readonly disabled = input<boolean>(false);
  readonly editable = input<boolean>(false);

  readonly lazy = input<boolean>(false);

  readonly filter = input<boolean>(false);
  readonly filterPlaceholder = input<string>('Szukaj');
  readonly filterMatchMode = input<'contains' | 'startsWith' | 'endsWith' | 'equals' | 'notEquals' | 'in' | 'lt' | 'lte' | 'gt' | 'gte'>('contains');

  readonly filterDebounceTime = input<number>(300);

  readonly focusOnHover = input<boolean>(true);
  readonly selectOnFocus = input<boolean>(false);
  readonly autoOptionFocus = input<boolean>(false);

  readonly virtualScroll = input<boolean>(false);
  readonly showLoading = input<boolean>(false);
  readonly lazyPageSize = input<AvailablePaginationSize>(10);

  readonly showErrorMessage = input<boolean>(true);

  readonly optionValue = input<string>('value');

  readonly display = input<MultiSelectDisplay>('comma');
  readonly showToggleAll = input<boolean>(true);
  readonly selectAll = input<boolean>(false);
  readonly maxSelectedLabels = input<number | null>(null);
  readonly selectedItemsLabel = input<string | undefined>(undefined);
  readonly selectionLimit = input<number | null>(null);
  readonly selectionLimitDisplay = input<MultiSelectSelectionLimitDisplay>('full');
  readonly showSelectAll = input<boolean>(true);
  readonly appendTo = input<string>('body');

  readonly selectionChange = output<MultiSelectSelectionChangeEvent<T>>();
  readonly rawSelectionChange = output<MultiSelectRawSelectionChangeEvent<T>>();
  readonly onFilter = output();
  readonly onFocus = output();
  readonly onBlur = output();
  readonly onClear = output();
  readonly onSelectAllChange = output<{ originalEvent: Event; checked: boolean }>();
  readonly onLoadMore = output<MultiSelectLoadMoreEvent>();

  readonly optionTemplate = contentChild<TemplateRef<any>>('optionTemplate');
  readonly selectedItemsTemplate = contentChild<TemplateRef<any>>('selectedItemsTemplate');
  readonly groupTemplate = contentChild<TemplateRef<any>>('groupTemplate');
  readonly headerTemplate = contentChild<TemplateRef<any>>('headerTemplate');
  readonly footerTemplate = contentChild<TemplateRef<any>>('footerTemplate');
  readonly emptyTemplate = contentChild<TemplateRef<any>>('emptyTemplate');
  readonly loaderTemplate = contentChild<TemplateRef<any>>('loaderTemplate');
  readonly filterTemplate = contentChild<TemplateRef<any>>('filterTemplate');
  readonly clearIconTemplate = contentChild<TemplateRef<any>>('clearIconTemplate');
  readonly dropdownIconTemplate = contentChild<TemplateRef<any>>('dropdownIconTemplate');
  readonly headerCheckboxIconTemplate = contentChild<TemplateRef<any>>('headerCheckboxIconTemplate');
  readonly itemCheckboxIconTemplate = contentChild<TemplateRef<any>>('itemCheckboxIconTemplate');
  readonly chipTemplate = contentChild<TemplateRef<any>>('chipTemplate');

  protected readonly isClosed = signal(false);
  protected readonly isDisabled = signal(false);
  protected readonly isLoading = signal<boolean>(false);
  protected readonly controlState = signal<{
    invalid: boolean;
    touched: boolean;
    dirty: boolean;
    errors: ValidationErrors | null;
  }>({
    invalid: false,
    touched: false,
    dirty: false,
    errors: null,
  });
  protected selected: WritableSignal<TValue[]> = signal<TValue[]>([]);
  protected readonly filterQuery = signal<string | null>(null);
  protected readonly availableOptions = signal<T[]>([]);

  private readonly requestedPages = new Set<number>();
  private readonly totalElements = signal<number | null>(null);

  protected readonly VIRTUAL_SCROLL_ITEM_SIZE: number = 45;
  protected readonly OPTION_LABEL: string = 'displayName';
  protected readonly OPTION_VALUE: string = 'value';
  protected readonly OPTION_DISABLED: string = 'disabled';
  protected readonly FILTER_BY: string = 'displayName';

  protected readonly filterEnabled = computed(() => this.filter() && !this.lazy());

  protected readonly isInvalid = computed(() => {
    const state = this.controlState();
    return state.invalid && (state.touched || state.dirty);
  });

  protected readonly errorMessage = computed(() => {
    const state = this.controlState();
    if (!state.errors) return null;
    return ValidationErrorsDictionary.resolveValidationErrors(state.errors);
  });

  protected readonly useFloatLabel = computed(() => this.labelMode() === 'float');
  protected readonly useIftaLabel = computed(() => this.labelMode() === 'ifta');
  protected readonly hasRequiredRule: Signal<boolean> = computed(() => {
    const state = this.controlState();
    return state.errors?.['required'] !== undefined;
  });

  protected ngControl: NgControl | null = null;

  constructor() {
    this.optionsTypeStrategy();
    this.initFilterDebounce();
    this.initLazyLoad();
    this.initCleanup();
    this.manageDisabledState();
  }

  public ngAfterViewInit(): void {
    this.handleNgControl();
  }

  public writeValue(value: TValue[] | null): void {
    this.selected.set(value ?? []);
  }

  public registerOnChange(fn: (value: TValue[] | null) => void): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    this.isDisabled.set(isDisabled);
  }

  protected handleLazyLoad($event: MultiSelectLazyLoadEvent): void {
    const pageSize = this.lazyPageSize();
    const currentLength = this.availableOptions().length;
    const total = this.totalElements();

    if (total !== null && currentLength >= total) return;

    if ($event.last >= currentLength - 1) {
      const nextPage = Math.floor(currentLength / pageSize);

      if (this.requestedPages.has(nextPage)) return;

      this.requestedPages.add(nextPage);
      this.isLoading.set(true);

      this.onLoadMore.emit({
        page: nextPage,
        size: pageSize,
        query: this.filterQuery(),
      });
    }
  }

  protected handleClear(): void {
    this.onClear.emit();
  }

  protected handleBlur($event: any): void {
    this.onTouched();
    this.isClosed.set(true);
    this.onBlur.emit();
    this.updateControlState();
  }

  protected handleFocus($event: any): void {
    this.onFocus.emit();
  }

  protected handleFilter($event: MultiSelectFilterEvent): void {
    const query = $event.filter?.trim() || null;

    if (this.lazy()) {
      this.filterSubject$.next(query);
    } else {
      this.filterQuery.set(query);
    }
  }

  protected handleChange($event: MultiSelectChangeEvent): void {
    const value = $event.value ?? [];

    this.onChange(value.length > 0 ? value : []);
    this.selectionChange.emit({originalEvent: $event.originalEvent, value});
    this.selected.set(value);

    const rawSelected = this.availableOptions().filter(option =>
      value.includes((option as any)[this.optionValue()])
    );
    this.rawSelectionChange.emit({originalEvent: $event.originalEvent, value: rawSelected});

    this.updateControlState();
  }

  protected handleSelectAllChange($event: any): void {
    this.onSelectAllChange.emit($event);
  }

  private onChange: (value: TValue[] | null) => void = () => {};

  private onTouched: () => void = () => {};

  private handleNgControl(): void {
    try {
      this.ngControl = this.injector.get(NgControl, null, {
        optional: true,
        self: true,
      });

      if (this.ngControl?.control) {
        const ctrl = this.ngControl.control;

        ctrl.statusChanges?.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
          this.updateControlState();
        });

        this.updateControlState();
      }
    } catch (e) {
      console.error(
        `[${MultiSelectInputComponent.name}] Could not find NgControl in parent injector tree. Make sure that you're using the component inside a form field. Error: ${e}`
      );
    }
  }

  private initCleanup(): void {
    this.destroyRef.onDestroy(() => {
      this.destroy$.next();
      this.destroy$.complete();
    });
  }

  private initFilterDebounce(): void {
    this.filterSubject$
      .pipe(debounceTime(this.filterDebounceTime()), takeUntil(this.destroy$))
      .subscribe((query) => {
        if (!this.lazy()) return;

        this.requestedPages.clear();
        this.totalElements.set(null);
        this.availableOptions.set([]);

        this.filterQuery.set(query);
        this.isLoading.set(true);

        this.requestedPages.add(0);
        this.onLoadMore.emit({
          page: 0,
          size: this.lazyPageSize(),
          query: query,
        });
      });
  }

  private initLazyLoad(): void {
    afterNextRender(() => {
      if (this.lazy()) {
        this.requestedPages.add(0);
        this.isLoading.set(true);
        this.onLoadMore.emit({
          page: 0,
          size: this.lazyPageSize(),
          query: null,
        });
      }
    });
  }

  private optionsTypeStrategy(): void {
    effect(
      (): void => {
        const options = this.options();

        this.isLoading.set(false);

        if (!options) return;

        untracked(() => {
          if (options instanceof Page) {
            this.handlePageableOptions(options);
          } else if (Array.isArray(options)) {
            if (options.length > 0 && options[0]?.toSelectText === undefined) {
              throw new Error(
                `[${MultiSelectInputComponent.name}] Model entities must implement Selectable interface`
              );
            }
            this.availableOptions.set(options);
          }
        });
      },
      {allowSignalWrites: true}
    );
  }

  private manageDisabledState(): void {
    effect(() => {
      this.isDisabled.set(this.disabled());
    });
  }

  private handlePageableOptions(pageableOptions: Page<T>): void {
    const {content, number, size, totalElements} = pageableOptions;

    this.totalElements.set(totalElements);

    if (number === 0) {
      this.availableOptions.set([...content]);
      return;
    }

    const current = this.availableOptions();
    const expectedLength = (number + 1) * size;

    if (current.length >= expectedLength) return;

    const existingValues = new Set(current.map(o => o.value));
    const newItems = content.filter(o => !existingValues.has(o.value));

    this.availableOptions.set([...current, ...newItems]);
  }

  private updateControlState(): void {
    if (this.ngControl?.control) {
      const ctrl = this.ngControl.control;
      this.controlState.set({
        invalid: ctrl.invalid,
        touched: ctrl.touched,
        dirty: ctrl.dirty,
        errors: ctrl.errors,
      });
    }
  }
}
